/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css } from 'aphrodite';
import { getFormattedDisplay } from 'blb-utils/date';
import { BlendBridge } from 'blb-types/blendBridge';
import { cardWrapperStyles, SectionCard } from '../SectionCard';
import { LoneIconButton, Tag, Tooltip } from '@blend/enterprise-components';
import { ENoteStatus, ENoteData } from '@blend-types/eclose';
import { downloadDocument } from 'blb-api/loans';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { useParams } from 'react-router-dom';

const statusToThemeMap: Record<ENoteStatus, { text: string; theme: Tag.Theme }> = {
  COMPLETE: { text: 'SIGNED', theme: 'default' },
  INVALID: { text: 'INVALID', theme: 'error' },
  READY_TO_SIGN: { text: 'PENDING BORROWER SIGNATURE', theme: 'warning' },
  SIGNER_WITHDRAWN: { text: 'INVALID', theme: 'error' },
};

const invalidTooltipText = 'This document has expired, a new version has been created, or the signer withdrew consent.';

export namespace ENoteDocumentCard {
  export interface Props {
    eNoteData: ENoteData;
    eNoteDoc?: BlendBridge.API.Document;
  }
}

export const ENoteDocumentCard: React.FC<ENoteDocumentCard.Props> = ({ eNoteData, eNoteDoc }) => {
  const { text, theme } = statusToThemeMap[eNoteData.eNoteStatus!];
  const StatusTag = <Tag text={text} theme={theme} />;
  const StatusTagWithTooltip = <Tooltip text={invalidTooltipText}>{StatusTag}</Tooltip>;
  const Toaster = useToasts();
  const { loanId } = useParams<{ loanId: string }>();

  const subtitle = eNoteData.noteDocumentInfo
    ? `Last updated on ${getFormattedDisplay(new Date(eNoteData.noteDocumentInfo.updatedAt).valueOf())}`
    : 'Last update unknown';

  const downloadENotePdf = async () => {
    if (!eNoteDoc) {
      return;
    }
    await downloadDocument({
      documentId: eNoteDoc._id,
      documentTypeLabel: 'PROMISSORY_NOTE',
      loanId,
      Toaster,
    });
  };

  const isEnoteDownloadable = () => {
    if (!eNoteDoc) {
      return false;
    }

    return Array.isArray(eNoteDoc.actions) && eNoteDoc.actions.includes('DOWNLOAD');
  };

  const isENoteViewable = () => {
    if (!eNoteDoc) {
      return false;
    }
    return Array.isArray(eNoteDoc.actions) && eNoteDoc.actions.includes('VIEW');
  };

  const viewENotePdf = () => {
    window.open(`/${loanId}/enote`, '_blank', 'noopener,noreferrer');
  };

  const canDownloadENote = isEnoteDownloadable();
  const canViewENote = isENoteViewable();

  const handleClick = async () => {
    if (canDownloadENote) {
      await downloadENotePdf();
    } else {
      if (canViewENote) {
        viewENotePdf();
      }
    }
  };

  return (
    <div className={css(cardWrapperStyles.singleCardWrapper)}>
      <SectionCard
        header="Promissory Note Status"
        headerChildren={eNoteData.eNoteStatus === 'INVALID' ? StatusTagWithTooltip : StatusTag}
        id="it-ClosingENote"
        sectionActions={
          eNoteDoc !== undefined && (
            <LoneIconButton
              icon={canDownloadENote ? 'save_alt' : 'visibility'}
              iconSize={24}
              id={canDownloadENote ? 'download-enote' : 'view-enote'}
              noBorder
              noHover
              onClick={handleClick}
              theme="tertiary"
            />
          )
        }
        subtitle={subtitle}
      />
    </div>
  );
};
